import { useRef, useEffect, useCallback } from 'react';
import {
  usePhotoConverterStore,
  MAX_PHOTOS,
} from '@square-photo/stores/photo-converter';
import type {
  PhotoConverterHook,
  PhotoConverterState,
} from '@square-photo/types/app-state.types';
import type {
  PhotoFile,
  ProcessedPhoto,
  ProcessingError,
} from '@square-photo/types/photo.types';
import type { WorkerResponse } from '@square-photo/types/worker.types';
import {
  generatePhotoId,
  isHeicFile,
  isSupportedFormat,
} from '@square-photo/utils/file.utils';
import {
  calcSquareDimension,
  calcPadding,
  PHOTO_PADDING_BACKGROUND,
} from '@square-photo/utils/image.utils';
import {
  createZipBlob,
  generateZipFilename,
} from '@square-photo/utils/zip.utils';

function getOriginalFormat(file: File): string {
  if (file.type === 'image/png') return 'png';
  return 'jpeg';
}

/**
 * 把一張照片交給 Worker 方形化，並以 Promise 包裝結果。
 *
 * bitmap 以 Transferable 傳遞，送出後主執行緒即失去所有權，由 Worker 在處理完
 * 呼叫 `bitmap.close()`。
 *
 * **`signal` 不可省略。** `worker.terminate()` 不會觸發任何事件，若在處理途中
 * 終止（例如元件卸載），這個 Promise 會永遠懸空，把整個 `startConvert` 的 async
 * 閉包連同 File 物件釘在記憶體裡。監聽 abort 主動 reject，閉包才結束得了。
 */
function sendToWorker(
  worker: Worker,
  photo: PhotoFile,
  bitmap: ImageBitmap,
  signal: AbortSignal,
): Promise<ProcessedPhoto> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject({ code: 'worker_error', message: 'Processing aborted' });
      return;
    }

    const cleanup = () => {
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);
      signal.removeEventListener('abort', onAbort);
    };

    const handleMessage = (event: MessageEvent<WorkerResponse>) => {
      if (event.data.id !== photo.id) return;
      cleanup();
      const { status, data, error } = event.data;
      if (status === 'success' && data) {
        resolve({
          id: photo.id,
          filename: data.filename,
          blob: data.blob,
          status: 'success',
        });
      } else {
        reject(
          error ?? {
            code: 'worker_error',
            message: 'Worker returned unknown error',
          },
        );
      }
    };

    const handleError = (err: ErrorEvent) => {
      cleanup();
      reject({ code: 'worker_error', message: err.message });
    };

    const onAbort = () => {
      cleanup();
      reject({ code: 'worker_error', message: 'Processing aborted' });
    };

    worker.addEventListener('message', handleMessage);
    worker.addEventListener('error', handleError);
    signal.addEventListener('abort', onAbort, { once: true });

    worker.postMessage(
      {
        id: photo.id,
        action: 'process-photo',
        data: {
          bitmap,
          filename: photo.file.name,
          originalFormat: getOriginalFormat(photo.file),
        },
      },
      [bitmap],
    );
  });
}

/** OffscreenCanvas 不支援時的降級路徑，改以 HTMLCanvasElement 在主執行緒方形化。 */
function processOnMainThread(
  photo: PhotoFile,
  bitmap: ImageBitmap,
): Promise<ProcessedPhoto> {
  return new Promise((resolve, reject) => {
    const { width, height } = bitmap;
    const squareDim = calcSquareDimension(width, height);
    const { top, left } = calcPadding(squareDim, width, height);

    const canvas = document.createElement('canvas');
    canvas.width = squareDim;
    canvas.height = squareDim;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      reject({ code: 'worker_error', message: 'Failed to get canvas context' });
      return;
    }

    ctx.fillStyle = PHOTO_PADDING_BACKGROUND;
    ctx.fillRect(0, 0, squareDim, squareDim);
    ctx.drawImage(bitmap, left, top, width, height);
    bitmap.close();

    const isPng = photo.file.type === 'image/png';
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject({
            code: 'worker_error',
            message: 'Failed to export canvas blob',
          });
          return;
        }
        const nameParts = photo.file.name.split('.');
        const ext = nameParts.pop()?.toLowerCase() ?? '';
        const base = nameParts.join('.');
        const finalExt =
          ext === 'heic' || ext === 'heif' ? 'jpg' : ext || 'jpg';
        const filename = `${base}_square.${finalExt}`;
        resolve({ id: photo.id, filename, blob, status: 'success' });
      },
      isPng ? 'image/png' : 'image/jpeg',
      isPng ? undefined : 0.92,
    );
  });
}

/**
 * 照片方形化的完整流程。
 *
 * 值本身存在 `usePhotoConverterStore`，這裡只管有副作用的部分：Worker 的生死、
 * Object URL 的建立與釋放、以及卸載時中止進行中的處理。
 *
 * 元件一律透過這支 hook 操作，不直接呼叫 store 的 action——那些 action 只做狀態
 * 轉換，單獨呼叫會讓畫面與 Worker、Object URL 的實際狀態對不上。
 */
export function usePhotoConverter(): PhotoConverterHook {
  const workerRef = useRef<Worker | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const {
    phase,
    photos,
    processedPhotos,
    currentProcessingIndex,
    zipFilename,
  } = usePhotoConverterStore();

  /* unmount：中止進行中的 Worker 處理、釋放所有 preview Object URL */
  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    return () => {
      controller.abort();
      abortControllerRef.current = null;
      /* getState() 取最新快照，不依賴閉包捕獲的 photos */
      usePhotoConverterStore
        .getState()
        .photos.forEach((p) => URL.revokeObjectURL(p.preview));
    };
  }, []);

  /* 僅在 client 且支援 OffscreenCanvas 時建立 Worker */
  useEffect(() => {
    if (typeof OffscreenCanvas === 'undefined') return;

    /*
     * `type: 'module'` 是必要的，不是風格選擇：worker 本身 import 了 utils 與型別，
     * 而 Vite 在 dev 下以原始 ES module 直接供應 worker 檔案（不預先打包）。
     * 用 classic worker 載入會在 `import` 那一行就 SyntaxError，症狀是轉換永遠
     * 停在第一張——而 build 產物剛好能動，因為那時已經打包過了。
     */
    const worker = new Worker(
      new URL('../workers/image-processor.worker.ts', import.meta.url),
      { type: 'module' },
    );
    workerRef.current = worker;

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  const addPhotos = useCallback(async (files: File[]) => {
    const currentCount = usePhotoConverterStore.getState().photos.length;
    const remaining = MAX_PHOTOS - currentCount;
    if (remaining <= 0) return;

    const validFiles = files.filter(isSupportedFormat).slice(0, remaining);

    const newPhotos: PhotoFile[] = await Promise.all(
      validFiles.map(async (file) => {
        let preview: string;
        if (isHeicFile(file)) {
          const { default: heic2any } = await import('heic2any');
          const converted = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.8,
          });
          const blob = Array.isArray(converted) ? converted[0] : converted;
          preview = URL.createObjectURL(blob as Blob);
        } else {
          preview = URL.createObjectURL(file);
        }
        return {
          id: generatePhotoId(),
          file,
          preview,
          status: 'idle' as const,
        };
      }),
    );

    if (newPhotos.length > 0) {
      usePhotoConverterStore.getState().addPhotos(newPhotos);
    }
  }, []);

  const removePhoto = useCallback((id: string) => {
    const photo = usePhotoConverterStore
      .getState()
      .photos.find((p) => p.id === id);
    if (photo) URL.revokeObjectURL(photo.preview);
    usePhotoConverterStore.getState().removePhoto(id);
  }, []);

  const startConvert = useCallback(async () => {
    const { photos: currentPhotos, phase: currentPhase } =
      usePhotoConverterStore.getState();
    if (currentPhotos.length === 0 || currentPhase !== 'selecting') return;

    /*
     * 進入 processing 前先對 photos 取快照。
     * 後續 setPhotoProcessing / setPhotoDone / setPhotoError 會改變 store，
     * 但迴圈的來源是此快照，確保迭代目標不變。
     */
    usePhotoConverterStore.getState().startProcessing(generateZipFilename());

    for (let i = 0; i < currentPhotos.length; i++) {
      const photo = currentPhotos[i];
      usePhotoConverterStore.getState().setPhotoProcessing(photo.id, i);

      try {
        /*
         * HEIC/HEIF 必須在主執行緒用 heic2any 解碼。
         * heic2any 依賴 DOM API（Canvas、Blob URL），無法在 Worker 執行。
         */
        let sourceBitmap: ImageBitmap;
        if (isHeicFile(photo.file)) {
          const { default: heic2any } = await import('heic2any');
          const converted = await heic2any({
            blob: photo.file,
            toType: 'image/jpeg',
            quality: 0.92,
          });
          const sourceBlob = Array.isArray(converted)
            ? converted[0]
            : converted;
          sourceBitmap = await createImageBitmap(sourceBlob as Blob);
        } else {
          sourceBitmap = await createImageBitmap(photo.file);
        }

        let processedPhoto: ProcessedPhoto;
        if (workerRef.current && typeof OffscreenCanvas !== 'undefined') {
          processedPhoto = await sendToWorker(
            workerRef.current,
            photo,
            sourceBitmap,
            abortControllerRef.current!.signal,
          );
        } else {
          processedPhoto = await processOnMainThread(photo, sourceBitmap);
        }

        usePhotoConverterStore.getState().setPhotoDone(processedPhoto);
      } catch (err) {
        const processingError: ProcessingError = {
          code:
            (err as { code?: string }).code === 'offscreen_canvas_unavailable'
              ? 'offscreen_canvas_unavailable'
              : 'worker_error',
          message: (err as { message?: string }).message ?? 'Processing failed',
        };
        usePhotoConverterStore
          .getState()
          .setPhotoError(photo.id, processingError);
      }
    }

    usePhotoConverterStore.getState().setAllDone();
  }, []);

  const sharePhotos = useCallback(async () => {
    const {
      processedPhotos: currentProcessed,
      zipFilename: currentZipFilename,
    } = usePhotoConverterStore.getState();
    const successPhotos = currentProcessed.filter(
      (p) => p.status === 'success',
    );
    if (successPhotos.length === 0) return;

    const files = successPhotos.map(
      (p) =>
        new File([p.blob], p.filename, { type: p.blob.type || 'image/jpeg' }),
    );

    if (navigator.canShare({ files })) {
      try {
        await navigator.share({ files, title: 'Square Photos' });
        return;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        /* NotAllowedError：Mac 桌面版的 Web Share API 不支援分享檔案，改走下載 */
        if (err instanceof Error && err.name !== 'NotAllowedError') throw err;
      }
    }

    const zipBlob = await createZipBlob(
      successPhotos.map((p) => ({ filename: p.filename, blob: p.blob })),
    );
    const url = URL.createObjectURL(zipBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = currentZipFilename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, []);

  const downloadZip = useCallback(async () => {
    const {
      processedPhotos: currentProcessed,
      zipFilename: currentZipFilename,
    } = usePhotoConverterStore.getState();
    const successPhotos = currentProcessed.filter(
      (p) => p.status === 'success',
    );
    if (successPhotos.length === 0) return;

    const zipBlob = await createZipBlob(
      successPhotos.map((p) => ({ filename: p.filename, blob: p.blob })),
    );

    const url = URL.createObjectURL(zipBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = currentZipFilename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, []);

  const reset = useCallback(() => {
    usePhotoConverterStore
      .getState()
      .photos.forEach((p) => URL.revokeObjectURL(p.preview));
    usePhotoConverterStore.getState().reset();
  }, []);

  const isMobile =
    typeof navigator !== 'undefined' &&
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const canSharePhotos =
    isMobile &&
    typeof navigator !== 'undefined' &&
    'share' in navigator &&
    'canShare' in navigator;

  const state: PhotoConverterState = {
    phase,
    photos,
    processedPhotos,
    currentProcessingIndex,
    zipFilename,
    canSharePhotos,
  };

  return [
    state,
    { addPhotos, removePhoto, startConvert, sharePhotos, downloadZip, reset },
  ];
}
